import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { BudgetBucket } from "./useBudgetData";
import { DollarSign, PieChart, ArrowRight, TrendingUp } from "lucide-react";
import { PieChart as Pie, Pie as PieComponent, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
interface BudgetSummaryProps {
  totalBudget: number;
  buckets: BudgetBucket[];
}
export const BudgetSummary: React.FC<BudgetSummaryProps> = ({
  totalBudget,
  buckets
}) => {
  // Calculate totals
  const allocatedTotal = buckets.reduce((sum, bucket) => sum + bucket.budget, 0);
  const allocatedPercent = totalBudget ? Math.min(allocatedTotal / totalBudget * 100, 100) : 0;
  const remainingBudget = totalBudget - allocatedTotal;

  // Sort buckets alphabetically
  const sortedBuckets = [...buckets].sort((a, b) => a.name.localeCompare(b.name));

  // Format data for pie chart
  const pieChartData = sortedBuckets.map(bucket => ({
    name: bucket.name,
    value: bucket.budget,
    allocation: totalBudget > 0 ? bucket.budget / totalBudget * 100 : 0
  }));

  // Calculate colors for each bucket (to match chart colors)
  const colors = ["#3B82F6", "#10B981", "#8B5CF6", "#F59E0B", "#EF4444", "#6366F1"];

  // Custom label for pie chart with percentages - Fixed to show correct allocation percentage
  const renderCustomizedLabel = (props: any) => {
    const {
      cx,
      cy,
      midAngle,
      innerRadius,
      outerRadius,
      percent,
      index,
      payload
    } = props;
    const RADIAN = Math.PI / 180;

    // Calculate position in the middle of the segment
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    // Get the actual data item to access the correct allocation percentage
    const item = pieChartData[index];

    // Always show label regardless of segment size with the correct allocation percentage
    return <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" className="text-[13px] font-medium">
        {item ? `${item.allocation.toFixed(1)}%` : ''}
      </text>;
  };
  return <div className="space-y-6">
      <Card className="bg-secondary/10 border-2 border-primary">
        <CardHeader>
          <CardTitle className="text-primary">Marketing Budget Allocation Summary</CardTitle>
          <CardDescription className="text-white">
            Breakdown of your marketing budget allocation across major categories
          </CardDescription>
        </CardHeader>
        <CardContent>
          {buckets.length === 0 ? <div className="text-center py-10 text-primary-light">
              <p>No budget data available. Start by allocating your marketing budget in the Manage Main Buckets sub tab.</p>
            </div> : <div className="flex flex-col md:flex-row gap-6">
              {/* Budget Allocation Summary - Left Side - 20% */}
              <div className="w-full md:w-[20%]">
                <div className="bg-secondary/5 rounded-lg p-4 space-y-4">
                  <h3 className="text-primary-light text-lg font-semibold">Budget Allocation Summary</h3>
                  
                  <div className="space-y-4">
                    {/* Budget Status */}
                    <div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-white">Total Budget:</span>
                        <span className="font-bold text-white">${totalBudget.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-white">Allocated:</span>
                        <span className="font-bold text-primary">${allocatedTotal.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-white">Remaining:</span>
                        <span className="font-bold text-yellow-500">${remainingBudget.toLocaleString()}</span>
                      </div>
                    </div>
                    
                    {/* Budget Progress */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm text-white">
                        <span>Allocation Progress</span>
                        <span className="text-primary">{Math.round(allocatedPercent)}%</span>
                      </div>
                      <Progress value={allocatedPercent} className="h-2" />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Pie Chart - Middle - 40% */}
              <div className="w-full md:w-2/5 h-[330px]">
                <ResponsiveContainer width="100%" height="100%">
                  <Pie>
                    <PieComponent data={pieChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={120} innerRadius={60} paddingAngle={2} label={renderCustomizedLabel} labelLine={false}>
                      {pieChartData.map((entry, index) => <Cell key={`cell-${index}`} fill={colors[index % colors.length]} stroke="#1A1F2C" strokeWidth={3} />)}
                    </PieComponent>
                    <Tooltip content={({
                  active,
                  payload
                }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return <div className="bg-secondary-light/90 p-4 border border-primary rounded-md backdrop-blur-sm shadow-xl">
                              <p className="text-black font-medium text-lg mb-2">{data.name}</p>
                              <div className="space-y-1">
                                <p className="text-black text-sm">Budget: <span className="text-primary font-medium">${data.value.toLocaleString()}</span></p>
                                <p className="text-black text-sm">Allocation: <span className="text-primary font-medium">{data.allocation.toFixed(1)}% of total</span></p>
                              </div>
                            </div>;
                  }
                  return null;
                }} />
                    <Legend layout="horizontal" verticalAlign="bottom" align="center" wrapperStyle={{
                  width: '100%'
                }} formatter={(value, entry, index) => <span className="text-primary-light text-xs mx-[2px]">{value}</span>} />
                  </Pie>
                </ResponsiveContainer>
              </div>
              
              {/* Budget Distribution Table - Right Side - 40% */}
              <div className="w-full md:w-[40%]">
                <div className="bg-secondary/5 rounded-lg p-4 h-full flex flex-col">
                  <h3 className="text-primary-light text-lg font-semibold mb-4">Budget Distribution</h3>
                  <div className="flex flex-col h-full">
                    <div className="sticky top-0 bg-secondary/20 z-10 w-full">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[60%] text-left text-primary-light text-[12px]">Category</TableHead>
                            <TableHead className="w-[15%] text-left text-primary-light text-[12px]">Budget</TableHead>
                            <TableHead className="w-[15%] text-left text-primary-light text-[12px]">%</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {sortedBuckets.map((bucket, index) => <TableRow key={bucket.id}>
                              <TableCell className="w-[70%] text-left text-white text-[12px]">{bucket.name}</TableCell>
                              <TableCell className="w-[15%] text-left text-white text-[12px]">${bucket.budget.toLocaleString()}</TableCell>
                              <TableCell className="w-[15%] text-left text-primary text-[12px]">
                                {totalBudget > 0 ? (bucket.budget / totalBudget * 100).toFixed(1) : '0.0'}%
                              </TableCell>
                            </TableRow>)}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </div>
              </div>
            </div>}
        </CardContent>
      </Card>
    </div>;
};